import React, { Fragment, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@shopflo/ui/sheet";
import { inIframe, handlePaymentSuccessRedirection, isMobile, classNames } from "lib/utils/helpers";
import { CheckoutModalType } from "lib/types/checkout";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { X } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
interface GenericDialogProps {
  isOpen: boolean;
  setIsOpen?: (value: boolean) => void;
  translateAxis?: "x" | "y" | "z";
  dialogOverlay?: boolean;
  customClass?: string;
  childClassName?: string;
  redirectURL?: string;
  modalType?: CheckoutModalType;
  closeOnOverlayClick?: boolean;
  showExternalClose?: boolean;
  children?: React.ReactNode;
  /** Radix focus-trap toggle. Only disable for dialogs that host a nested portal
   * (e.g. FinalFamilyTray) whose content would otherwise sit outside the trap's DOM scope
   * and get its focus yanked back on every click. */
  modal?: boolean;
  /** Skip this dialog's own close transition. Radix keeps a closing Sheet (and its overlay div,
   * which isn't itself animated) mounted for the full exit-animation duration, so when the popup
   * orchestrator swaps `activePopup` straight from this dialog to another one (e.g. OOS paused for
   * a nested ITEM_PROPERTIES dialog), both dialogs' `bg-black/40` overlays are briefly stacked,
   * reading as a darker backdrop that then snaps lighter once this one finishes unmounting. Passing
   * true collapses that window to ~0 by zeroing the exit transition/animation duration. Only meant
   * for the render where this dialog is closing because another one just took over - leave false so
   * a genuine dismissal (nothing else opening) keeps its normal slide-down animation.
   */
  disableExitAnimation?: boolean;
  /** Override Radix's open auto-focus (which focuses the first tabbable element). Call
   * `e.preventDefault()` and focus your own element. Needed when the first tabbable is a native
   * <select> (e.g. react-phone-number-input's hidden country select): iOS Safari opens the picker
   * wheel as soon as a <select> gets focus. */
  onOpenAutoFocus?: (e: Event) => void;
}

export const GenericDialog = React.memo(
  ({
    isOpen,
    setIsOpen,
    translateAxis = "z",
    dialogOverlay = false,
    customClass = "h-full md:h-[85vh]",
    childClassName = "",
    children,
    modalType,
    redirectURL,
    closeOnOverlayClick = false,
    showExternalClose = false,
    modal = true,
    disableExitAnimation = false,
    onOpenAutoFocus,
  }: GenericDialogProps) => {
    const {
      state: { checkoutModal },
      actions: { setCheckoutModal },
    } = useCheckoutContext();

    useEffect(() => {
      if (!Boolean(isOpen)) return;

      //Nested modals will have the modalType of their parent.
      if (checkoutModal === "NONE" && modalType) {
        setCheckoutModal(modalType);

        return () => {
          setCheckoutModal("NONE");
        };
      }
    }, [isOpen]);

    const handleOpenChange = (open: boolean) => {
      if (!open && closeOnOverlayClick) {
        setIsOpen?.(false);
      }
    };

    // Determine sheet side based on translateAxis
    const getSheetSide = () => {
      switch (translateAxis) {
        case "x":
          return "right";
        case "y":
          return "bottom";
        default:
          return "bottom";
      }
    };

    return (
      <Sheet open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
        <SheetContent
          side={getSheetSide()}
          withinContainer
          container={document.getElementById("checkout-ui-root")}
          className={`${customClass} ${isMobile() ? "rounded-b-none" : ""}`}
          overlayClassName="!bg-black/40"
          onOpenAutoFocus={onOpenAutoFocus}
          style={{
            ...(showExternalClose ? { overflow: "visible" } : {}),
            // `animation: "none"` (not just a 0-duration override) so Radix's Presence sees
            // animationName === "none" and unmounts synchronously - a 0-duration animation
            // never fires `animationend`, which Presence would otherwise wait on forever.
            ...(!isOpen && disableExitAnimation ? { animation: "none", transition: "none" } : {}),
          }}>
          {showExternalClose && (
            <button
              type="button"
              aria-label="Close"
              onClick={() => setIsOpen?.(false)}
              className="absolute -top-12 left-1/2 z-[60] flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full text-white outline-none focus:outline-none focus-visible:outline-none"
              style={{
                background: "rgba(255, 255, 255, 0.12)",
                border: "1px solid rgba(255, 255, 255, 0.24)",
                backdropFilter: "blur(6px)",
                WebkitTapHighlightColor: "transparent",
              }}>
              <Icon IconComponent={X} size="xs" iconProps={{ weight: "bold" }} />
            </button>
          )}
          <div
            className={classNames(
              `flex flex-col h-full relative ${modalType === "EXIT_CONFIRMATION" ? "overflow-visible" : ""}`,
              childClassName,
            )}>
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  },
);

export const DialogHeader = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, style, children }, ref) => {
    return (
      <header
        ref={ref}
        style={style}
        className={classNames(
          `absolute inset-x-0 top-0 z-20 flex min-h-[48px] w-full items-center overflow-hidden rounded-t-3xl border-r-inherit bg-white px-6  md:w-[25rem] ${className} ${
            !inIframe() && "md:w-[25rem]"
          }`,
        )}>
        {children}
      </header>
    );
  },
);
DialogHeader.displayName = "DialogHeader";

export const DialogBody = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children }, ref) => {
    const footerRandomId: string = Math.random().toString(36).substr(2);
    const [hasFooter, setHasFooter] = useState<boolean>(false);
    useEffect(() => {
      const sectionEl = document.getElementById(footerRandomId);
      const footerEl = sectionEl?.querySelector("footer");
      if (footerEl) {
        setHasFooter(true);
      }
    }, [footerRandomId]);

    return (
      <section
        ref={ref}
        id={footerRandomId}
        className={`h-full w-full pt-16 ${
          Boolean(hasFooter) ? "pb-44" : "pb-4"
        } !overflow-y-auto scrollbar-hide ${className}`}>
        {children}
      </section>
    );
  },
);
DialogBody.displayName = "DialogBody";

export const DialogFooter = ({ children, className }: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <footer
      className={`fixed w-full inset-x-0 bottom-0 z-20 m-auto max-h-36 rounded-b-[28px] bg-gradient-to-b from-transparent via-white to-white px-3 py-4 md:bottom-[64px] ${className} ${
        isMobile() ? "rounded-b-none" : ""
      } ${!inIframe() && "md:w-[25rem]"}`}>
      {children}
    </footer>
  );
};
